import React, { ReactElement, useMemo, useState } from 'react';

import { getClassNames, noOp } from '../../../../../utils/util';
import { ENGAGEMENT, HASHTAGS } from '../../../../../utils/constants';

import { HashTag } from '../../../../../types/entity.types';
import { ActivityDataTemplate, TableRowData } from '../../../../../types/common.types';

import ProgrammeLayout from '../../../../../layouts/programme/ProgrammeLayout';
import Table from '../../../../common/table/Table';
import DatePicker from '../../../../common/input/date-picker/DatePicker';

import { ReactComponent as ArrowIcon } from '../../../../../resources/icons/arrow-single.svg';

import './tribe-member-engagement.scss';

const ACTIVITY_DATA_TEMPLATE: ActivityDataTemplate = {
  '#': '',
  username: '',
  lastSeen: '',
  postsCreated: '',
  likes: '',
  comments: '',
  all: ''
};

type HashtagDataTemplate = Record<string, string>;

type Props = {
  activityData?: ActivityDataTemplate[];
  hashtagData?: unknown[];
  hashtags?: HashTag[];
  applyDateFilter: (date: Date | undefined, toDate: Date | undefined) => void;
  navigateBack: () => void;
};

const TribeMemberEngagement = ({
  activityData = [],
  hashtagData = [],
  hashtags = [],
  applyDateFilter,
  navigateBack
}: Props): ReactElement => {
  const [tab, setTab] = useState<string>(ENGAGEMENT);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);

  const HASHTAG_DATA_TEMPLATE: HashtagDataTemplate = useMemo((): HashtagDataTemplate => {
    const template: HashtagDataTemplate = {
      '#': '',
      username: ''
    };

    hashtags.forEach((hashtag: HashTag): void => {
      if (!hashtag.text) {
        return;
      }

      template[hashtag.text] = '';
    });

    return template;
  }, [hashtags]);

  const handleFromDateChange = (date: Date): void => {
    setFromDate(date);
    applyDateFilter(date, toDate);
  };

  const handleToDateChange = (date: Date): void => {
    setToDate(date);
    applyDateFilter(fromDate, date);
  };

  const renderTableTopActions = (): ReactElement => {
    return (
      <div className="tribe-member-engagement__table-top-actions">
        <label htmlFor="from-date">From</label>
        <DatePicker name="from-date" value={fromDate} onChange={handleFromDateChange} />
        <label htmlFor="to-date">To</label>
        <DatePicker name="to-date" value={toDate} onChange={handleToDateChange} />
      </div>
    );
  };

  const renderTable = (): ReactElement => {
    const isEngagement: boolean = tab === ENGAGEMENT;

    return (
      <div className={getClassNames('tribe-member-engagement__table', { engagement: isEngagement })}>
        <Table
          onDeleteRowClick={noOp}
          data={isEngagement ? activityData : (hashtagData as TableRowData[])}
          dataTemplate={isEngagement ? ACTIVITY_DATA_TEMPLATE : HASHTAG_DATA_TEMPLATE}
          rowsPerPage={8}
          topActions={renderTableTopActions()}
          filter
        />
      </div>
    );
  };

  return (
    <ProgrammeLayout>
      <div className="tribe-member-engagement" data-testid="tribe-member-engagement">
        <div className="tribe-member-engagement__header">
          <ArrowIcon
            className="tribe-member-engagement__header__arrow-icon"
            onClick={navigateBack}
            data-testid="arrow-icon"
          />
          <h1>Member Engagement</h1>
        </div>
        <div className="tribe-member-engagement__scroll-container">
          <div className="tribe-member-engagement__toggle-container">
            <div
              className={getClassNames('tribe-member-engagement__toggle-container__toggle', {
                toggled: tab === ENGAGEMENT
              })}
              onClick={(): void => setTab(ENGAGEMENT)}
            >
              Engagement
            </div>
            <div
              className={getClassNames('tribe-member-engagement__toggle-container__toggle', {
                toggled: tab === HASHTAGS
              })}
              onClick={(): void => setTab(HASHTAGS)}
            >
              Hashtags
            </div>
          </div>
          {renderTable()}
        </div>
      </div>
    </ProgrammeLayout>
  );
};

export default TribeMemberEngagement;
